'use client';

import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NaceCodeItem } from '@/lib/backend-api';
import { industryClassificationBlocks } from '@/lib/company-classifications';
import { cn } from '@/lib/utils';

export function IndustryClassificationsDisclosure({ naceCodes }: { naceCodes: NaceCodeItem[] }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const blocks = industryClassificationBlocks(naceCodes, i18n.language);
  if (blocks.length === 0) return null;

  return (
    <div className="min-w-0 text-[10px] sm:text-xs">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((value) => !value)}
        className="inline-flex min-h-10 max-w-full items-center gap-1 text-left text-muted-foreground hover:text-foreground focus-visible:outline-ring"
      >
        <ChevronDown aria-hidden="true" className={cn('size-3 shrink-0', expanded && 'rotate-180')} />
        {t('companyDetail.industryVersions.label')}
      </button>
      <div id={contentId} hidden={!expanded} className="min-w-0 space-y-2 break-words">
        {blocks.map((block, index) => (
          <div key={`${block.code}-${index}`} className="min-w-0 space-y-1">
            <h4 className="font-medium text-foreground">WZ 2025 {block.code}</h4>
            <ul className="space-y-0.5">
              {block.rows.map((row, rowIndex) => (
                <li key={`${row.scheme}-${rowIndex}`} className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                  <span className="text-muted-foreground">{row.label}</span>
                  {row.codes.map((code, codeIndex) => (
                    <span key={`${code.code}-${codeIndex}`} title={code.title} className="min-w-0 text-foreground [overflow-wrap:anywhere]">
                      {code.code}{codeIndex < row.codes.length - 1 ? ',' : ''}
                    </span>
                  ))}
                  {row.remaining > 0 && <span className="text-muted-foreground">+{row.remaining}</span>}
                  {row.partial && (
                    <span title={t('companyDetail.industryVersions.partialTooltip')} className="text-muted-foreground">
                      {t('companyDetail.industryVersions.partial')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {block.source && (
              <p title={block.source.title} className="text-muted-foreground [overflow-wrap:anywhere]">
                {t('companyDetail.industryVersions.reportedAs', { scheme: block.source.scheme, code: block.source.code })}
              </p>
            )}
          </div>
        ))}
        <p className="text-muted-foreground">{t('companyDetail.industryVersions.footnote')}</p>
      </div>
    </div>
  );
}
